import {Action, Actions, Button, Card, Separator, Tooltip} from 'platform/components';
import {Grid, Heading, Hide, HStack, Show, Text, VStack} from 'platform/foundation';

import {GetCustomerContractResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

interface DiscountsTabCustomerContractCardProps extends TestIdProps {
  customerContract: GetCustomerContractResponse | Nullish;
  isEditingDisabled: boolean;
  canEditDirectSale: boolean;
  onAssign: () => void;
  onView: () => void;
  onChange: () => void;
  onDeactivate: () => void;
}

export function DiscountsTabCustomerContractCard(props: DiscountsTabCustomerContractCardProps) {
  const isEditDisabled = props.isEditingDisabled || !props.canEditDirectSale;
  const missingPermissionLabel = props.canEditDirectSale
    ? undefined
    : i18n.t('module.warehouse.label.missingDirectSaleEditPermission');

  const actions: Action[] = [
    {
      type: 'button',
      title: i18n.t('general.actions.view'),
      onClick: props.onView,
      buttonType: 'button',
      variant: 'link',
      leftIcon: 'image/remove_red_eye',
      'data-testid': suffixTestId('viewDetail', props),
    },
    {
      type: 'button',
      title: i18n.t('general.actions.change'),
      onClick: props.onChange,
      isDisabled: isEditDisabled,
      tooltip: missingPermissionLabel,
      buttonType: 'button',
      variant: 'link',
      leftIcon: 'navigation/refresh',
    },
    {
      type: 'button',
      title: i18n.t('general.actions.deactivate'),
      onClick: props.onDeactivate,
      isDisabled: isEditDisabled,
      tooltip: missingPermissionLabel,
      buttonType: 'button',
      variant: 'dangerLink',
      leftIcon: 'navigation/close',
    },
  ];

  return (
    <Card variant="inlineGrey">
      <VStack spacing={4}>
        <VStack spacing={1}>
          <HStack justify="space-between" spacing={4}>
            <Heading size={4}>{i18n.t('entity.warehouse.labels.customerContract')}</Heading>
            <Show when={props.customerContract}>
              {/* Keeps the heading row as high as in the other cards. The 32 px links overflow it evenly. */}
              <HStack height={6} align="center">
                <Actions
                  actions={actions}
                  data-testid={suffixTestId('customerContractActions', props)}
                />
              </HStack>
            </Show>
          </HStack>
          <Text size="small" color="secondary">
            {i18n.t('entity.warehouse.labels.customerContractDescription')}
          </Text>
        </VStack>
        <Separator spacing={0} />
        <Show when={props.customerContract}>
          {(customerContract) => (
            <Grid columns={4}>
              <VStack>
                <Text size="small">{i18n.t('general.labels.name')}</Text>
                <Heading size={4}>{customerContract.name}</Heading>
              </VStack>
              <VStack>
                <Text size="small">{i18n.t('general.labels.description')}</Text>
                <Heading size={4}>{customerContract.description}</Heading>
              </VStack>
              <VStack>
                <Text size="small">
                  {i18n.t('entity.warehouse.labels.customerGroupForMaterialDelivery')}
                </Text>
                <Heading size={4}>{customerContract.materialDiscountGroup?.name}</Heading>
              </VStack>
            </Grid>
          )}
        </Show>
        <Hide when={props.customerContract}>
          <HStack>
            <Tooltip
              isDisabled={props.canEditDirectSale}
              label={i18n.t('module.warehouse.label.missingDirectSaleEditPermission')}
            >
              <Button
                onClick={props.onAssign}
                variant="secondary"
                title={i18n.t('entity.warehouse.labels.assignContract')}
                isDisabled={isEditDisabled}
                data-testid={suffixTestId('assignContract', props)}
              />
            </Tooltip>
          </HStack>
        </Hide>
      </VStack>
    </Card>
  );
}
